import React, { useEffect, useRef, useState } from "react";
import "./Home.css";
import { useNavigate } from "react-router-dom";

const Home = ({ setPage }) => {
  const journeyPathRef = useRef(null);
  const countryTrackRef = useRef(null);
  const countryGroupRef = useRef(null);

  const [pathLength, setPathLength] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const navigate = useNavigate();

  const AnimatedCounter = ({ end, duration = 1800 }) => {
    const [count, setCount] = useState(0);
    const counterRef = useRef(null);
    const hasAnimatedRef = useRef(false);



    useEffect(() => {
      const node = counterRef.current;
      if (!node) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting || hasAnimatedRef.current) return;

          hasAnimatedRef.current = true;
          let startTime = null;

          const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;

            const progress = Math.min((currentTime - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const value = Math.floor(eased * end);

            setCount(value);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(end);
            }
          };

          requestAnimationFrame(animate);
        },
        { threshold: 0.35 }
      );

      observer.observe(node);

      return () => observer.disconnect();
    }, [end, duration]);

    return <span ref={counterRef}>{count}</span>;
  };


  useEffect(() => {
    const track = countryTrackRef.current;
    const group = countryGroupRef.current;

    if (!track || !group) return;

    let animationFrameId;
    let lastTime = performance.now();
    let offset = 0;
    let groupWidth = 0;

    const speed = 18; // lower = slower, higher = faster

    const updateWidth = () => {
      groupWidth = group.getBoundingClientRect().width;
    };

    const animate = (currentTime) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      if (groupWidth > 0) {
        offset += (speed * deltaTime) / 1000;

        if (offset >= groupWidth) {
          offset = offset - groupWidth;
        }

        track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    updateWidth();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(updateWidth);
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(group);

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
    };
  }, []);
  useEffect(() => {
    if (journeyPathRef.current) {
      setPathLength(journeyPathRef.current.getTotalLength());
    }

    const handleScroll = () => {
      const homeEl = document.querySelector(".home-page");

      if (!homeEl) return;

      const rect = homeEl.getBoundingClientRect();
      const totalScrollable = homeEl.offsetHeight - window.innerHeight;
      const scrolledInsideHome = Math.min(
        Math.max(-rect.top, 0),
        totalScrollable
      );

      const progress =
        totalScrollable > 0 ? scrolledInsideHome / totalScrollable : 0;

      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const techGroups = [
    {
      title: "Frontend",
      items: [
        "HTML",
        "CSS",
        "JavaScript",
        "React.js",
        "Responsive UI",
        "API Integration",
      ],
    },
    {
      title: "Backend",
      items: [
        "Python",
        "FastAPI",
        "REST APIs",
        "Authentication",
        "Backend Logic",
      ],
    },
    {
      title: "Database",
      items: [
        "MongoDB",
        "Database Design",
        "Queries",
        "Data Modeling",
        "CRUD Operations",
      ],
    },
    {
      title: "DevOps & Tools",
      items: [
        "Linux",
        "GitHub",
        "Docker",
        "Kubernetes",
        "CI/CD",
        "Deployment",
        "Server Basics",
      ],
    },
    {
      title: "AI, Data Science & GenAI",
      items: [
        "AI",
        "Data Science",
        "Python for AI",
        "NumPy",
        "Pandas",
        "Data Analysis",
        "Data Visualization",
        "Machine Learning",
        "Deep Learning",
        "Neural Networks",
        "Computer Vision",
        "NLP",
        "GenAI",
        "LLMs",
        "Prompt Engineering",
        "RAG",
        "Vector Databases",
        "AI Agents",
        "Agentic AI",
        "MLOps",
        "Model Deployment",
        "AI Project Building",
      ],
    },
  ];




  const countries = [
    { name: "USA", code: "us" },
    { name: "Canada", code: "ca" },
    { name: "UK", code: "gb" },
    { name: "France", code: "fr" },
    { name: "Germany", code: "de" },
    { name: "Sweden", code: "se" },
    { name: "Norway", code: "no" },
    { name: "UAE", code: "ae" },
    { name: "Singapore", code: "sg" },
    { name: "Australia", code: "au" },
    { name: "South Korea", code: "kr" },

    { name: "Netherlands", code: "nl" },
    { name: "Ireland", code: "ie" },
    { name: "Switzerland", code: "ch" },
    { name: "Japan", code: "jp" },
    { name: "New Zealand", code: "nz" },
    { name: "Finland", code: "fi" },
    { name: "Denmark", code: "dk" },
    { name: "Spain", code: "es" },
    { name: "Italy", code: "it" },
    { name: "Austria", code: "at" },

    { name: "And more", code: "nl" },
  ];
  const roles = [
    "AI Engineer",
    "Data Scientist",
    "Machine Learning Engineer",
    "Deep Learning Engineer",
    "Computer Vision Engineer",
    "NLP Engineer",
    "GenAI Engineer",
    "LLM Engineer",
    "AI Agent Developer",
    "MLOps Engineer",
    "Full Stack Developer",
    "Python Backend Developer",
    "React Developer",
    "React Native Developer",
    "Software Engineer",
    "Data Analyst",
    "Business Analyst",
    "Automation Engineer",
    "AI Product Engineer",
    "Cloud & Deployment Engineer",
    "And many more...",
  ];


  const goToPage = (page) => {
    const pageRoutes = {
      Counselling: "/counselling",
      GlobalTalent: "/global-talent",
      IndustryExperience: "/industry-experience",
      GraduatesRoadmap: "/graduates",
      WorkingProfessionals: "/working-professionals",
      GraduationStudents: "/college-students",
      AfterTenthTwelfth: "/10th-12th",
      StudentLogin: "/student-login",
      SuccessStoriesNewsletter: "/success-stories-newsletter",
    };

    navigate(pageRoutes[page] || "/");
  };



  // const renderCountryFlag = (country) => {
  //   if (!country.code) {
  //     return <span className="country-world-icon">🌍</span>;
  //   }

  //   return (
  //     <img
  //       src={`https://flagcdn.com/w40/${country.code}.png`}
  //       alt={`${country.name} flag`}
  //       className="country-flag-img"
  //       loading="lazy"
  //     />
  //   );
  // };

  return (
    <main className="home-page">
      {/* WANDERING NEON JOURNEY PATH */}
      {/* DYNAMIC WANDERING NEON JOURNEY PATH */}
      <div className="journey-path-wrap">
        <svg
          className="journey-svg"
          viewBox="0 0 1200 6200"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <filter id="journeyGlow">
              <feGaussianBlur stdDeviation="10" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <linearGradient id="journeyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#eaff00" />
              <stop offset="42%" stopColor="#00e5ff" />
              <stop offset="100%" stopColor="#ff2f92" />
            </linearGradient>
          </defs>
          <circle className="journey-dot dot-0" cx="620" cy="0" r="8" />

          {/* soft secondary curves for dynamic feeling */}
          <path
            className="journey-ghost journey-ghost-one"
            d="
        M 240 360
        C 930 760, 100 1110, 880 1510
        C 150 1910, 1050 2360, 270 2790
        C 960 3190, 170 3650, 930 4070
        C 200 4510, 990 4940, 310 5390
        C 850 5740, 560 6040, 710 6200
      "
          />

          <path
            className="journey-ghost journey-ghost-two"
            d="
        M 940 500
        C 170 900, 990 1320, 240 1770
        C 920 2140, 210 2600, 980 3060
        C 260 3420, 910 3840, 270 4300
        C 1000 4750, 140 5200, 820 5650
        C 430 5900, 520 6100, 480 6200
      "
          />

          {/* base track */}
          <path
            className="journey-track"
            d="
  M 620 0
  C 920 260, 250 360, 895 520
  C 600 820, 340 790, 420 1160
  C 520 1510, 980 1340, 765 1780
  C 560 2180, 215 2040, 335 2460
  C 460 2880, 1050 2660, 905 3130
  C 760 3560, 335 3340, 485 3820
  C 630 4280, 1065 3980, 875 4520
  C 700 5010, 240 4740, 395 5300
  C 520 5750, 875 5480, 690 6180
"
          />

          {/* progress line */}
          <path
            ref={journeyPathRef}
            className="journey-progress"
            d="
  M 620 0
  C 920 260, 250 360, 895 520
  C 600 820, 340 790, 420 1160
  C 520 1510, 980 1340, 765 1780
  C 560 2180, 215 2040, 335 2460
  C 460 2880, 1050 2660, 905 3130
  C 760 3560, 335 3340, 485 3820
  C 630 4280, 1065 3980, 875 4520
  C 700 5010, 240 4740, 395 5300
  C 520 5750, 875 5480, 690 6180
"
            style={{
              strokeDasharray: pathLength,
              strokeDashoffset:
                pathLength -
                pathLength *
                Math.min(
                  window.innerWidth <= 768
                    ? 0.05 + scrollProgress * 0.95
                    : 0.08 + scrollProgress * 0.92,
                  1
                ),
            }}
          />

          <circle className="journey-dot dot-1" cx="895" cy="520" r="8" />
          <circle className="journey-dot dot-2" cx="420" cy="1160" r="8" />
          <circle className="journey-dot dot-3" cx="765" cy="1780" r="8" />
          <circle className="journey-dot dot-4" cx="335" cy="2460" r="8" />
          <circle className="journey-dot dot-5" cx="905" cy="3130" r="8" />
          <circle className="journey-dot dot-6" cx="485" cy="3820" r="8" />
          <circle className="journey-dot dot-7" cx="875" cy="4520" r="8" />
          <circle className="journey-dot dot-8" cx="395" cy="5300" r="8" />
        </svg>
      </div>
      {/* HERO */}
      <section className="hero-section home-career-hero">
        <div className="hero-glow hero-glow-one" />
        <div className="hero-glow hero-glow-two" />

        <div className="hero-content">

          <p className="home-hero-kicker">
            PLATFORM WITH ONE SINGLE GOAL
          </p>

          <h1 className="home-global-title">
            <span className="home-global-main-line">
              To Prepare You for Salary Packages of
            </span>

            <span className="home-global-salary-line">
              Up to ₹1 Crore Per Year
            </span>
          </h1>

          <p className="home-global-subtitle">
            In AI, Data Science & Full Stack
          </p>


          {/* GLOBAL COUNTRY STRIP */}
          <div className="home-global-country-strip">
            <div className="home-global-country-track">

              {[...countries, ...countries].map((country, index) => (

                <div
                  className="home-global-country"
                  key={`${country.name}-${index}`}
                >

                  <img
                    src={`https://flagcdn.com/w80/${country.code}.png`}
                    alt={`${country.name} flag`}
                  />

                  <span>
                    {country.name}
                  </span>

                </div>

              ))}

            </div>
          </div>


          <p className="home-career-choice-text">
            Choose Where You Stand Today and Take the Right Path Towards Your
            Target Salary Package
          </p>

          {/* THREE CAREER PATHS */}
          <div className="home-career-paths">

            {/* PATH 1 */}
            <div className="home-career-path-card skilled-path">

              <h2>I Have Tech / AI Experience</h2>

              <p className="career-path-summary">
                Already working professionally
              </p>

              <button
                className="career-path-btn global-jobs-btn"
                onClick={() => goToPage("GlobalTalent")}
              >
                Submit My Resume →
              </button>

            </div>

            {/* PATH 2 */}
            <div className="home-career-path-card experience-path">

              <h2>I Have Coding Skills but No Experience</h2>

              <p className="career-path-summary">
                Build real industry experience
              </p>

              <button
                className="career-path-btn experience-btn"
                onClick={() => goToPage("IndustryExperience")}
              >
                Build My Experience →
              </button>

            </div>

            {/* PATH 3 */}
            <div className="home-career-path-card learning-path">

              <h2>I’m New to Tech with No Skills or Experience</h2>

              <p className="career-path-summary">
                Study, Build real industry experience
              </p>

              <button
                className="career-path-btn learning-btn"
                onClick={() => navigate("/study-roadmap")}
              >
                Start My Tech Career →
              </button>

            </div>


          </div>

        </div>
      </section>









      {/* MISSION */}
      <section className="section mission-section">
        <div className="section-heading">
          <p className="section-kicker">OUR MISSION</p>
          <h2>A career system for serious learners.</h2>
          <p>
            We guide students who are ready to study hard, stay disciplined,
            build real skills, and prepare for high-growth technology careers.
          </p>
        </div>

        <div className="mission-grid">
          <div className="glass-card mission-card">
            <span className="card-number">01</span>
            <h3>Learn with direction</h3>
            <p>
              No random course hopping. Students follow a clear learning path
              based on their current stage and career goal.
            </p>
          </div>

          <div className="glass-card mission-card">
            <span className="card-number">02</span>
            <h3>Build practical skill</h3>
            <p>
              Focus on coding, AI, Full Stack, projects, debugging, portfolio,
              and technical confidence.
            </p>
          </div>

          <div className="glass-card mission-card">
            <span className="card-number">03</span>
            <h3>Prepare for real roles</h3>
            <p>
              Training is aligned toward interviews, professional communication,
              GitHub profile, and career readiness.
            </p>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      {/* FOUNDER */}
      {/* FOUNDER */}
      <section className="section founder-section">
        <div className="founder-premium-card">
          <div className="founder-main-grid">
            {/* LEFT PHOTO CARD */}
            <div className="founder-photo-card">
              <div className="founder-photo-frame">
                <img src="/akshay-founder.jpg" alt="B. Akshay - Founder and Mentor" />
              </div>

              <div className="founder-profile-text">
                <h3>B. Akshay</h3>
                <p>AI Research Data Scientist</p>
                <span>Founder & Mentor, StudyAIPlacement</span>
              </div>
            </div>

            {/* RIGHT INTRO */}


            <div className="founder-intro-content">

              <p className="section-kicker ">GUIDED BY INDUSTRY EXPERIENCE</p>



              <h2>Teaching serious students to study hard with discipline.</h2>



              <p>

                StudyAIPlacement is guided by <strong>B. Akshay</strong>, a working AI

                Research Data Scientist with <strong>13+ years</strong> across AI,

                Data Science, Deep Learning, and Software Development.

              </p>



              <p>

                The focus is simple: teach students with discipline, make them study hard,

                build practical projects, prepare for interviews, improve their portfolio,

                and guide them with long-term career clarity.

              </p>

            </div>
          </div>



          {/* STATS STRIP */}
          <div className="founder-stats-strip">
            <div className="founder-stat-box">
              <strong>
                <AnimatedCounter end={13} />+
              </strong>
              <span>Years across AI, Data Science & Software Development</span>
            </div>

            <div className="founder-stat-box">
              <strong>
                <AnimatedCounter end={350} />+
              </strong>
              <span>Students guided since 2021</span>
            </div>

            <div className="founder-stat-box">
              <strong>
                <AnimatedCounter end={320} />+
              </strong>
              <span>Students supported toward placement</span>
            </div>
          </div>

          {/* CAREER PATH */}
          <div className="career-path-card">
            <div className="career-path-heading">
              <p className="section-kicker">CAREER PATH</p>

              <h3>From intern to AI Research Data Scientist — a real 13-year climb.</h3>
            </div>

            <div className="career-path-line">
              <div className="career-step">
                <span>2013</span>
                <p>Data Science Intern</p>
              </div>

              <div className="career-step">
                <span>2015</span>
                <p>AI Software Developer</p>
              </div>

              <div className="career-step">
                <span>2016</span>
                <p>Data Scientist</p>
              </div>

              <div className="career-step">
                <span>2019</span>
                <p>AI Developer</p>
              </div>

              <div className="career-step">
                <span>2021</span>
                <p>Deep Learning Developer</p>
              </div>


              <div className="career-step active">

                <span>2022 — Current</span>
                <p>AI Research Data Scientist</p>
              </div>


            </div>
          </div>

          {/* TRUST FOCUS CARDS */}
          <div className="founder-trust-grid">
            <div className="founder-trust-card">
              <span>01</span>
              <h3>Practical AI exposure</h3>
              <p>
                Guidance comes from real AI, Data Science, and software development
                experience.
              </p>
            </div>

            <div className="founder-trust-card">
              <span>02</span>
              <h3>Project-based learning</h3>
              <p>
                Students focus on building skills through projects, debugging,
                deployment, and portfolio work.
              </p>
            </div>

            <div className="founder-trust-card">
              <span>03</span>
              <h3>Career-focused mentorship</h3>
              <p>
                The goal is not only learning, but interview readiness, confidence,
                and long-term career direction.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* STUDY ROADMAP */}
      <section className="section home-roadmap-simple" id="study-roadmap">
        <div className="home-roadmap-simple-inner">
          <p className="section-kicker">STUDY ROADMAP</p>

          <h2>
            Find the roadmap that fits your current stage.
          </h2>

          <p>
            See your study routine, syllabus timeline and complete
            interview-to-placement journey.
          </p>

          <button
            onClick={() => {
              window.location.href = "/study-roadmap";
            }}
          >
            View My Study Roadmap →
          </button>
        </div>
      </section>

      {/* COUNTRIES */}
      <section className="section countries-section">
        <div className="section-heading">

          <p className="section-kicker">CAREER OPPORTUNITY COUNTRIES</p>

          <h2>Build skills in India. Prepare for opportunities worldwide.</h2>
          <p>
            We help students prepare for technology careers in India and global countries
            through practical learning, portfolio building, interview readiness, and
            consistent career guidance.
          </p>
        </div>

        <div className="country-grid">
          {[...countries, ...countries].map((country, index) => (
            <div className="country-card" key={country.name}>
              <span className="country-flag">{country.flag}</span>
              <span>{country.name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* TECHNOLOGIES */}
      <section className="section tech-section">
        <div className="section-heading">
          <p className="section-kicker">SKILLS YOU WILL BUILD</p>
          <h2>AI + Full Stack + deployment mindset.</h2>
          <p>
            The goal is not only to learn tools, but to build confidence to use
            them in practical projects and interviews.
          </p>
        </div>

        <div className="tech-grid">
          {techGroups.map((group) => (
            <div className="tech-card glass-card" key={group.title}>
              <h3>{group.title}</h3>
              <div className="tech-tags">
                {group.items.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ROLES */}
      <section className="section roles-section">
        <div className="section-heading">
          <p className="section-kicker">TARGET CAREER ROLES</p>
          <h2>Prepare for modern AI, software, and data roles.</h2>
          <p>
            These are some of the major career roles students can prepare for through
            AI, Data Science, Full Stack, and deployment-focused learning. Based on
            skill, consistency, and project quality, students can also explore many more
            technology roles.
          </p>
        </div>

        <div className="roles-grid">
          {roles.map((role) => (
            <div className="role-card" key={role}>
              {role}
            </div>
          ))}
        </div>
      </section>

      {/* SUPPORT */}
      <section className="section support-section">
        <div className="support-box">
          <div className="support-heading">
            <p className="section-kicker">CAREER SUPPORT SYSTEM</p>
            <h2>Learning is only one part. Career preparation completes the journey.</h2>
            <p>
              We support students beyond lectures with profile building, interview
              preparation, job application strategy, progress tracking, and career
              counselling.
            </p>
          </div>

          <div className="support-grid">
            <span>Resume Building</span>
            <span>GitHub Portfolio</span>
            <span>LinkedIn Guidance</span>
            <span>Mock Interviews</span>
            <span>HR Preparation</span>
            <span>Job Application Strategy</span>
            <span>Progress Review</span>
            <span>Career Counselling</span>
            <span>Portfolio Projects</span>
            <span>Communication Practice</span>
            <span>Interview Confidence</span>
            <span>Placement Direction</span>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section why-us-section">
        <div className="why-us-box">
          <div className="section-heading why-us-heading">
            <p className="section-kicker">WHY STUDYAIPLACEMENT</p>
            <h2>Not just coaching. A serious AI career preparation system.</h2>
            <p>
              In the current journey of ChatGPT, Claude, GenAI, and AI automation,
              students need more than normal coaching. They need discipline, practical
              skill, AI awareness, project execution, research mindset, and career
              survival preparation.
            </p>

            <p>
              Many students do not fail because they cannot learn. They fail because
              they study without direction, discipline, projects, interview preparation,
              and proper career guidance.
            </p>
          </div>

          <div className="why-compare-grid">
            <div className="why-compare-card weak">
              <p className="compare-label">WHY NOT RANDOM COACHING?</p>
              <h3>Videos and certificates are not enough anymore.</h3>

              <ul>
                <li>Many courses only teach tools, not career direction.</li>
                <li>Students watch lectures but do not build discipline.</li>
                <li>Projects, GitHub, LinkedIn, and interview preparation are often weak.</li>
                <li>Most students are not trained to survive the AI disruption.</li>
                <li>AI tools like ChatGPT and Claude are changing how companies hire and work.</li>
              </ul>
            </div>

            <div className="why-compare-card strong">
              <p className="compare-label">WHY STUDYAIPLACEMENT?</p>
              <h3>We prepare students for the real AI-driven future.</h3>

              <ul>
                <li>Guided by working AI and software industry experience.</li>
                <li>Discipline-first learning with serious study expectations.</li>
                <li>AI, Data Science, Full Stack, deployment, and career preparation together.</li>
                <li>Students learn how to use AI tools smartly instead of fearing them.</li>
                <li>Focus on projects, portfolio, interview confidence, and long-term career clarity.</li>
              </ul>
            </div>
          </div>

          <div className="why-us-grid">
            <div className="why-us-card">
              <span>01</span>
              <h3>Discipline-first learning</h3>
              <p>
                We push students to study seriously, stay consistent, complete tasks,
                and build the habit required for a real technology career.
              </p>
            </div>

            <div className="why-us-card">
              <span>02</span>
              <h3>Working AI guidance</h3>
              <p>
                Students are guided with practical exposure across AI, Data Science,
                Deep Learning, Software Development, and real project thinking.
              </p>
            </div>

            <div className="why-us-card">
              <span>03</span>
              <h3>AI tools survival mindset</h3>
              <p>
                We help students understand how ChatGPT, Claude, GenAI, and automation
                are changing careers, and how to prepare instead of getting replaced.
              </p>
            </div>

            <div className="why-us-card">
              <span>04</span>
              <h3>Research paper direction</h3>
              <p>
                Students can be guided toward research thinking, problem selection,
                technical writing, experimentation, and AI research paper preparation.
              </p>
            </div>

            <div className="why-us-card">
              <span>05</span>
              <h3>AI patent awareness</h3>
              <p>
                We introduce students to innovation thinking, idea documentation,
                problem solving, and the mindset required for AI patent-oriented work.
              </p>
            </div>

            <div className="why-us-card">
              <span>06</span>
              <h3>AI conference exposure</h3>
              <p>
                Students are encouraged to understand AI events, technical communities,
                paper discussions, project showcases, and professional networking.
              </p>
            </div>

            <div className="why-us-card">
              <span>07</span>
              <h3>AI community building</h3>
              <p>
                We help students grow with peer learning, technical discussions,
                project sharing, doubt solving, and consistent community support.
              </p>
            </div>

            <div className="why-us-card">
              <span>08</span>
              <h3>Portfolio and proof of work</h3>
              <p>
                Students are guided to build GitHub, LinkedIn, resumes, projects, case
                studies, and visible proof of skill instead of only certificates.
              </p>
            </div>

            <div className="why-us-card">
              <span>09</span>
              <h3>Interview and communication</h3>
              <p>
                We focus on technical confidence, HR preparation, communication
                practice, mock interviews, and explaining projects properly.
              </p>
            </div>

            <div className="why-us-card">
              <span>10</span>
              <h3>Full Stack plus AI</h3>
              <p>
                Students learn not only AI models but also software development,
                backend, frontend, databases, deployment, and product-building mindset.
              </p>
            </div>

            <div className="why-us-card">
              <span>11</span>
              <h3>Personal roadmap clarity</h3>
              <p>
                Every student starts from a different stage. We guide them based on
                their background, current skill level, and career target.
              </p>
            </div>

            <div className="why-us-card">
              <span>12</span>
              <h3>Admission for serious students only</h3>
              <p>
                StudyAIPlacement is not for students who only want certificates. It is for
                students who are ready to study hard, follow discipline, complete projects,
                use AI smartly, and seriously prepare for better career opportunities.
              </p>
            </div>


          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="final-cta">
        <div className="final-cta-box">
          <p className="section-kicker">START YOUR CAREER ROADMAP</p>

          <h2>Ready to study seriously and build your tech career?</h2>

          <p>
            Start with counselling, understand your current stage, and choose the
            roadmap that fits your career goal.
          </p>

          <button onClick={() => goToPage("Counselling")}>
            Apply for Counselling
          </button>
        </div>
      </section>
    </main>
  );
};

export default Home;